// Detalle de colección — pantalla a donde lleva "Ver más" de cada
// CollectionRow en la página principal de Interfaz V2.
//
// Layout simple: header con back button + título de la colección, y
// una grilla de presets (2 cols mobile / 3-4 cols desktop) que ocupa
// todo el viewport. Tocar un preset navega a `/interfaz-v2` y pasa
// `{ presetId, collectionId }` en location.state — el efecto en
// InterfazV2 abre el sheet en step 'preview' con esa colección como
// previewList, así el user puede swipear / navegar a otras plantillas
// sin volver acá.
//
// Chrome del header mobile:
// El Sidebar móvil usa `mobileHeaderTransparent` para mostrar el
// chrome flotante (back/menu en pills blancas + counter de créditos),
// como en la home y el preset sheet. Acá lo activamos para que esta
// pantalla se sienta parte del mismo flow y no muestre "Interfaz V2"
// como título genérico arriba.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSidebar } from '../../context/SidebarContext';
import type { CollectionV2, PresetV2 } from './mockData';
import { fetchTemplatesV2 } from './templatesApi';
import { fetchAllPoses } from './posesApi';
import { buildVistasSoloProductoPresets } from './vistasCatalogo';

// Rubros que ven la categoría virtual "E-commerce" (poses como cards).
// Espejo de `DESIGN_RUBROS` en InterfazV2 — duplicado para no crear
// dependencia circular con el archivo de la home.
const DESIGN_RUBROS = new Set([
  'Moda e Indumentaria',
  'Belleza y Cuidado Personal',
]);

// Orden custom del row de E-commerce: cuerpo_completo + plano_medio
// primero, detalle_close-up al final, resto en el medio.
const ecommerceCategoryRank = (cat: string | null): number => {
  if (cat === 'cuerpo_completo') return 0;
  if (cat === 'plano_medio') return 1;
  if (cat === 'detalle_close-up') return 3;
  return 2;
};

// Cache global de las colecciones — sobrevive a mounts/unmounts dentro
// de la sesión así, al volver desde un preset, vemos la grilla al
// instante en vez del skeleton. Se actualiza con cada fetch fresh.
const COLLECTIONS_CACHE_KEY = 'atlas-v2-collections-cache';
const SCROLL_KEY = (slug: string) => `atlas-v2-col-scroll-${slug}`;

export const ColeccionDetalle: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const {
    setMobileHeaderTransparent,
    setMobileTitleOverride,
    setMobileHideBack,
    setMobileBackOverride,
  } = useSidebar();

  // Page transition: slide-in desde la derecha al montar, slide-out
  // hacia la derecha al hacer back. Override del back del Sidebar para
  // disparar la animación antes de navegar. Solo se aplica en mobile —
  // en desktop la animación CSS está gated detrás de un media query y
  // el `goBack` navega inmediatamente sin esperar el delay.
  const [exiting, setExiting] = useState(false);
  const goBack = React.useCallback(() => {
    const isMobile = window.innerWidth < 640;
    if (!isMobile) {
      navigate(-1);
      return;
    }
    setExiting(true);
    // 260ms = duración del keyframe pageSlideOutRight
    window.setTimeout(() => navigate(-1), 260);
  }, [navigate]);

  // Header mobile: chrome flotante (transparent) + sin título — replica
  // la home / preset sheet. El back aparece automático en pill blanca
  // y el counter de créditos también, gestionados por Sidebar.tsx.
  useEffect(() => {
    setMobileHeaderTransparent(true);
    setMobileTitleOverride(null);
    setMobileHideBack(false);
    setMobileBackOverride(() => goBack);
    return () => {
      setMobileHeaderTransparent(false);
      setMobileTitleOverride(null);
      setMobileHideBack(false);
      setMobileBackOverride(null);
    };
  }, [
    setMobileHeaderTransparent,
    setMobileTitleOverride,
    setMobileHideBack,
    setMobileBackOverride,
    goBack,
  ]);

  // Restore scroll position: si el user había guardado scroll antes de
  // entrar a un preset (vía openPreset), lo restauramos. Sino scroll
  // al tope (entrada fresca desde otra ruta).
  useEffect(() => {
    if (!slug) return;
    try {
      const sy = sessionStorage.getItem(SCROLL_KEY(slug));
      if (sy) {
        const y = Number(sy);
        sessionStorage.removeItem(SCROLL_KEY(slug));
        // Defer al próximo frame para que la grilla ya esté renderizada
        // (sino el documento es más corto que la posición guardada).
        requestAnimationFrame(() => {
          window.scrollTo({ top: y });
          document.documentElement.scrollTo({ top: y });
        });
        return;
      }
      window.scrollTo({ top: 0 });
      document.documentElement.scrollTo({ top: 0 });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [slug]);

  // Fetch templates de la DB filtrados por user (mismo helper que en la
  // home). Encontramos la colección por slug — el slug viene del URL y
  // matchea el `template_categorias.slug`. Initializer lee el cache
  // de sessionStorage: si el user ya visitó esta página antes en la
  // sesión, mostramos la data al toque (sin skeleton).
  const [collections, setCollections] = useState<CollectionV2[] | null>(() => {
    try {
      const raw = sessionStorage.getItem(COLLECTIONS_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed as CollectionV2[];
      }
    } catch {
      /* noop */
    }
    return null;
  });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Resolver rubro + companyId del user (mismo flow que InterfazV2).
        const { data: { user } } = await supabase.auth.getUser();
        let rubro: string | null = null;
        let companyId: string | null = null;
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company')
            .eq('id', user.id)
            .single();
          const idCompany = (profile as any)?.id_company as string | undefined;
          if (idCompany) {
            companyId = idCompany;
            const { data: company } = await supabase
              .from('company_profiles')
              .select('rubro')
              .eq('id_company', idCompany)
              .single();
            rubro = (company as any)?.rubro || null;
          }
        }
        const list = await fetchTemplatesV2({ rubro, companyId });
        // Si el slug es la categoría virtual "e-commerce" (poses), la
        // armamos del fetch a `poses` — no vive en `template_categorias`.
        // El rubro debe calificar (sino la card ni siquiera aparece en
        // la home, pero igual respetamos la guarda).
        const wantsEcommerce =
          slug === 'e-commerce' && rubro
            ? DESIGN_RUBROS.has(rubro)
            : false;
        let withEcommerce = list;
        if (wantsEcommerce) {
          try {
            const poses = await fetchAllPoses();
            if (poses.length > 0) {
              const ecommerce: CollectionV2 = {
                id: 'e-commerce',
                title: 'Fondo blanco',
                subtitle: undefined,
                presets: [
                  ...[...poses]
                    .sort(
                      (a, b) =>
                        ecommerceCategoryRank(a.categoria) -
                        ecommerceCategoryRank(b.categoria),
                    )
                    .map(
                      (p): PresetV2 => ({
                        id: `pose-${p.id}`,
                        title: p.nombre,
                        imageUrl: p.imageUrl,
                        imageUrlFull: p.imageUrl,
                        kind: 'design' as const,
                      }),
                    ),
                  // Vistas "solo producto" (mismo catálogo que Catálogo
                  // IA) al final — cards kind 'design-producto'.
                  ...buildVistasSoloProductoPresets(),
                ],
              };
              withEcommerce = [ecommerce, ...list];
            }
          } catch {
            // silent — si falla, la lookup por slug devuelve undefined
            // y la página muestra "Colección no encontrada".
          }
        }
        if (alive) {
          setCollections(withEcommerce);
          try {
            sessionStorage.setItem(
              COLLECTIONS_CACHE_KEY,
              JSON.stringify(withEcommerce),
            );
          } catch {
            /* noop */
          }
        }
      } catch {
        if (alive) setCollections((prev) => prev ?? []);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  const collection = collections?.find((c) => c.id === slug);

  // Overlay blanco mobile + bg suave desktop — mismo patrón que la home
  // de Interfaz V2 (InterfazV2.tsx). NO aplicar `paddingLeft: sidebarWidth`
  // acá; `DashboardLayout` ya maneja el offset del sidebar.
  const MobileOverlay = (
    <div
      className="fixed inset-0 bg-white pointer-events-none sm:hidden"
      aria-hidden="true"
    />
  );

  // Skeleton: 8 placeholders shimmer mientras el fetch resuelve, con
  // el mismo aspect-[3/4] que las cards reales y una franja chica
  // arriba simulando el título de la colección. Mantiene la altura
  // estable para que el layout no salte cuando termina de cargar.
  if (collections === null) {
    return (
      <>
        {MobileOverlay}
        <div className="relative min-h-[100dvh] bg-surface-mobile sm:bg-surface-content">
          <div className="max-w-content mx-auto px-5 sm:px-8 pt-8 sm:pt-12 pb-24">
            <header className="mb-6">
              <div className="hidden sm:block w-16 h-4 rounded-md bg-black/[0.06] mb-4 animate-pulse" />
              <div className="w-2/3 h-9 sm:h-10 rounded-md bg-black/[0.06] animate-pulse" />
            </header>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-[3/4] rounded-xl bg-black/[0.06] animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!collection) {
    return (
      <>
        {MobileOverlay}
        <div className="relative min-h-[100dvh] bg-surface-mobile sm:bg-surface-content flex items-center justify-center">
          <div className="text-center max-w-sm px-6">
            <p className="text-[17px] font-semibold text-brand-black/90 mb-2">
              Colección no encontrada
            </p>
            <p className="text-[14px] text-[#3C3C43]/60 mb-5">
              El link al que llegaste no corresponde a ninguna colección activa.
            </p>
            <button
              type="button"
              onClick={() => navigate('/interfaz-v2')}
              className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full bg-brand-primary text-white text-[14px] font-medium hover:bg-brand-primary-hover transition-colors"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {MobileOverlay}
      <div
        className={`relative min-h-[100dvh] bg-surface-mobile sm:bg-surface-content ${
          exiting ? 'page-slide-out-right' : 'page-slide-in-right'
        }`}
        style={{ willChange: 'transform' }}
      >
      <div className="max-w-content mx-auto px-5 sm:px-8 pt-8 sm:pt-12 pb-24">
        {/* Header */}
        <header className="mb-6">
          {/* "Volver" oculto en mobile — en esa breakpoint el header
              global (Sidebar mobile bar) ya tiene su propio botón de back
              ("<" arriba a la izquierda), así que este sería redundante. */}
          <button
            type="button"
            onClick={goBack}
            className="hidden sm:inline-flex items-center gap-1 text-brand-primary text-[14px] mb-4 hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
          <h1 className="text-[24px] sm:text-[32px] font-semibold text-brand-black/90 leading-tight">
            {collection.title}
          </h1>
        </header>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {collection.presets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => {
                // Guardamos scroll para restaurar al volver — sin esto
                // ColeccionDetalle se monta de nuevo al hacer back y
                // empieza scrolleado hasta arriba.
                if (slug) {
                  try {
                    sessionStorage.setItem(
                      SCROLL_KEY(slug),
                      String(window.scrollY),
                    );
                  } catch {
                    /* noop */
                  }
                }
                navigate(`/interfaz-v2?preset=${preset.id}`, {
                  state: {
                    presetId: preset.id,
                    collectionId: collection.id,
                    presetData: preset,
                    collectionPresets: collection.presets,
                  },
                });
              }}
              className="aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 relative group active:scale-[0.98] transition-transform"
            >
              {preset.outputType === 'video' ? (
                <video
                  src={preset.imageUrlFull || preset.imageUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster={preset.imageUrl}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                />
              ) : (
                <img
                  src={preset.imageUrl}
                  alt={preset.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                />
              )}
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
              <p className="absolute bottom-2.5 left-3 right-3 text-left text-white text-[13px] font-medium leading-tight drop-shadow">
                {preset.title}
              </p>
            </button>
          ))}
        </div>
      </div>
      </div>
    </>
  );
};

export default ColeccionDetalle;
